import { Member, MemberStatusType, User } from '@/types';

import { MemberActions, MemberState } from '../types';

type MemberStoreState = MemberState & MemberActions;

export const selectMembers = (state: MemberStoreState) => state.members;
export const selectSelectedMember = (state: MemberStoreState) => state.selectedMember;
export const selectMembersLoading = (state: MemberStoreState) => state.loading;
export const selectSelectedMemberLoading = (state: MemberStoreState) => state.loadingMember;
export const selectMemberError = (state: MemberStoreState) => state.error;
export const selectFetchMembers = (state: MemberStoreState) => state.fetchMembers;

export const selectMemberById = (memberId?: string) => (state: MemberStoreState) =>
    memberId ? state.members.find((member) => member.id === memberId) : undefined;

export const selectMemberByUserId = (userId?: string) => (state: MemberStoreState) =>
    userId ? state.members.find((member) => member.user?.id === userId) : undefined;

interface WorkspaceMemberDirectoryInput {
    members: readonly Member[];
    selectedMember?: Member | null;
    workspaceId?: string;
}

const belongsToWorkspace = (member: Member, workspaceId?: string) =>
    !workspaceId || member.workspace?.id === workspaceId;

const memberIdentityKey = (member: Member) => member.user?.id || member.id;

const compareMembersByName = (left: Member, right: Member) => {
    const memberLabel = (member: Member) =>
        [member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ') ||
        member.user?.emailAddress ||
        member.id;

    return memberLabel(left).localeCompare(memberLabel(right), undefined, { sensitivity: 'base' });
};

/**
 * Builds the canonical, active roster for a workspace.
 *
 * The selected membership is merged because it is fetched independently during
 * authentication. Dedupe by user identity prevents historical duplicate member
 * rows from leaking into people pickers while retaining member ids for mutations.
 */
export const deriveActiveWorkspaceMembers = ({
    members,
    selectedMember,
    workspaceId,
}: WorkspaceMemberDirectoryInput): Member[] => {
    const roster = new Map<string, Member>();

    [...members, ...(selectedMember ? [selectedMember] : [])].forEach((member) => {
        if (
            member.status !== MemberStatusType.ACTIVE ||
            !member.user?.id ||
            !belongsToWorkspace(member, workspaceId)
        ) {
            return;
        }

        const identity = memberIdentityKey(member);
        const existing = roster.get(identity);

        // Prefer the independently hydrated active member over a stale list row.
        roster.set(identity, selectedMember?.id === member.id ? member : (existing ?? member));
    });

    return [...roster.values()].sort(compareMembersByName);
};

export const deriveActiveWorkspaceUsers = (input: WorkspaceMemberDirectoryInput): User[] =>
    deriveActiveWorkspaceMembers(input).map((member) => member.user);
